import React, { useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  Tooltip,
  TableRow,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { Link, useLocation } from "react-router-dom";
import { integraBoton } from "../../../services/MyPatientsServices";
import Fab from "@mui/material/Fab";
import PostAddOutlinedIcon from "@mui/icons-material/PostAddOutlined";
import { GenerateRecipeButton } from "../../GenerateRecipeButton";
import { useSnackbar } from 'notistack';
import { getDecryptedDataFromSessionStorage } from "../../../utils";
import { SECRET_KEY } from "../../../constants/encriptKey";

export const MyPatientsListResults = ({ patients, loading, ...rest }) => {
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [loadingGenerateRecipe, setLoadingGenerateRecipe] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const data = getDecryptedDataFromSessionStorage(SECRET_KEY);
  const { idMedico } = data


  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handlePageChange = (e, newPage) => {
    setPage(newPage);
  };

  const handleGenerarReceta = async (patient) => {
    // const idMedico = localStorage.getItem("idMedico");
    const currentLocation = location.pathname;
    const formatDate = (date) => {
      if (!date) return "Fecha no válida";

      const fecha = new Date(date);
      if (isNaN(fecha.getTime())) return "Fecha no válida";
      const year = fecha.getFullYear();
      const month = String(fecha.getMonth() + 1).padStart(2, "0");
      const day = String(fecha.getDate()).padStart(2, "0");
  
      return `${year}-${month}-${day}`;
    };
  
    const data = {
      idMedico: idMedico,
      nombre: patient.nombre,
      apellido: patient.apellido,
      tipoDocumento: patient.tipoDocumento,
      nroDocumento: patient.nroDocumento,
      fechaNacimiento: formatDate(patient.fechaNacimiento),
      email: patient.email,
      telefono: patient.telefono,
      sexo: patient.sexo,
      nombreFinanciador: patient.nombreFinanciador,
      nroFinanciador: patient.nroFinanciador,
      nroAfiliado: patient.nroAfiliado,
      plan: patient.plan,
      urlCallback: currentLocation,
    };
    
    setLoadingGenerateRecipe(true);
  
    try {
      const response = await integraBoton(data);
  
      if (response.statusResult === 200) {
        //window.open(response.result.url, "_blank");
        window.location.href = response.result.url;
      } else {
        if (Array.isArray(response.errors) && response.errors.length > 0) {
          enqueueSnackbar(response.errors[0], { variant: "error" });
        } 
      }
    } catch (error) {
      console.error("Error en el servicio integraBoton:", error);
      enqueueSnackbar("Ocurrió un error en el servicio", { variant: "error" });
    } finally {
      setLoadingGenerateRecipe(false);
    }
  };

  const showGenerateRecipeButton = (patient) =>
    loadingGenerateRecipe ? (
      <CircularProgress
        color="primary"
        size={20}
        sx={{
          marginLeft: "8px",
        }}
      />
    ) : (
      <GenerateRecipeButton onClick={() => handleGenerarReceta(patient)} />
    );

  return (
    <Card
      sx={{
        border: "0.5px solid #dddddd",
        padding: "1px",
        boxShadow: "1px 1px #d5d5d5",
      }}
      {...rest}
    >
        <Box sx={{ overflow: "auto" }}>
          <Box sx={{ width: "100%", display: "table", tableLayout: "fixed" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Apellido</TableCell>
                  <TableCell>Tipo Documento</TableCell>
                  <TableCell>Nro Documento</TableCell>
                  <TableCell>Financiador</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Nro Credencial</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      Acciones
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {!loading &&
                  patients?.length > 0 &&
                  patients
                    .slice(page * limit, page * limit + limit)
                    .map((patient) => (
                      <TableRow hover key={patient.pacienteFinanciadorId}>
                        <TableCell>{patient.nombre}</TableCell>
                        <TableCell>{patient.apellido}</TableCell>
                        <TableCell>{patient.tipoDocumento}</TableCell>
                        <TableCell>{patient.nroDocumento}</TableCell>
                        <TableCell>{patient.nombreFinanciador}</TableCell>
                        <TableCell>{patient.plan}</TableCell>
                        <TableCell>{patient.nroAfiliado}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              alignItems: "center",
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <Tooltip title="Ver detalles">
                              <Link
                                to={`/my-patients/view/${patient.idPaciente}`}
                              >
                                <IconButton
                                  sx={{
                                    marginRight: "8px",
                                  }}
                                  color="primary"
                                >
                                  <VisibilityOutlinedIcon />
                                </IconButton>
                              </Link>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <Link
                                to={`/my-patients/edit/${patient.idPaciente}`}
                              >
                                <IconButton
                                  sx={{
                                    marginRight: "8px",
                                  }}
                                  color="primary"
                                >
                                  <ModeEditIcon />
                                </IconButton>
                              </Link>
                            </Tooltip>
                            <Tooltip title="Generar receta">
                              {showGenerateRecipeButton(patient)}
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                {!loading && (!patients || patients.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      <Alert severity="info">
                        No hay pacientes para mostrar
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>

      <Box
        sx={{
          alignItems: "center",
          padding: "10px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {!!loading && (
          <div>
            <CircularProgress />
          </div>
        )}
      </Box>
      <TablePagination
        component="div"
        count={(patients && patients.length) || 0}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleLimitChange}
        page={page}
        rowsPerPage={limit}
        labelRowsPerPage="Filas por página"
        rowsPerPageOptions={[5, 10, 25]}
      />
    </Card>
  );
};

MyPatientsListResults.propTypes = {
  patients: PropTypes.array,
};
